import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../styles/table-style";
import BASE_URL from "../../../../assets/global/url";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import FilterStore from "../../../../hooks/customHook/FilterStore";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter";
import { useServerSort } from "../../../../hooks/customHook/sortingHook/useTableSort";

function NewStockCategoryTab({ category }) {
  const navigate = useNavigate();
  // For search filter
  const debounceTimer = useRef(null);
  const pageRestored = useRef(false);
  const [searchTerm, setSearchTerm] = useState(() =>
    FilterStore.SCTgetSearch(`stockcategorytab`),
  );
  useEffect(() => {
    FilterStore.SCTsaveSearch(searchTerm, `stockcategorytab`);
  }, [searchTerm]);
  // PARA ITO SA FILTER RETENTION
  //START
  const [filterColumn, setFilterColumn] = useState(() =>
    FilterStore.getFilterColumn(),
  );
  useEffect(() => {
    FilterStore.saveFilterColumn(filterColumn);
  }, [filterColumn]);

  // For warehouse
  const [selectedWarehouse, setSelectedWarehouse] = useState(() =>
    FilterStore.getWarehouse(),
  );
  //END
  const warehouseOptions = useRef([]);

  // For widgets
  const [widgets, setWidgets] = useState({
    totalQuantity: 0,
    overallPrice: 0,
  });

  // For fetchings
  const stockManagementEndpoint = `${BASE_URL}/stockManagement`;
  const productsPagination = useServerPagination(
    "about:blank",
    FilterStore.SCTgetPerPage(),
  );

  // No initial sortBy: the products query has no default ORDER BY,
  // so every header starts neutral until the user picks one
  const sort = useServerSort(productsPagination);

  // Get the stock management widgets
  const getWidgets = async () => {
    try {
      const res = await axios.get(`${stockManagementEndpoint}/widgets`, {
        params: {
          productCategory: category,
        },
      });

      setWidgets((prev) => ({
        ...prev,
        ...res.data,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  // Get the stock management product's table
  const getProducts = (
    warehouseId = selectedWarehouse,
    searchText = searchTerm,
    column = filterColumn,
  ) => {
    const hasSearch = searchText && searchText.trim() !== "";

    productsPagination.updateApiUrl(
      hasSearch
        ? `${stockManagementEndpoint}/products/category-summary/search`
        : `${stockManagementEndpoint}/products/category-summary`,
    );
    productsPagination.updateParams({
      productCategory: category,
      warehouseId,
      ...(hasSearch && { filterColumn: column, searchTerm }), /// FOR FILTER RETENTION FOR COLUMN
    });
  };

  // Fetch warehouse options
  const fetchWarehouse = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/warehouse/getWarehouse`);
      warehouseOptions.current = res.data;
    } catch (error) {
      console.log(error);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      productsPagination.updateApiUrl(
        `${stockManagementEndpoint}/products/category-summary/search`,
      );
      productsPagination.updateParams({
        productCategory: category,
        warehouseId: selectedWarehouse,
        filterColumn,
        searchTerm: e.target.value,
      });
    }, 500);
  };

  // Handle warehouse change
  const handleWarehouseChange = (event) => {
    const value = event.target.value;
    setSelectedWarehouse(value);
    FilterStore.saveWarehouse(value); //FILTER RETENTION LOCAL STORAGE TO FILTERSTORE
    getProducts(value);
  };

  // Handle row click
  const handleRowClicked = (row) => {
    navigate(
      `/inventory/stockMangement/rawMaterialUpdate?productId=${row.product_id}&warehouseId=${row.warehouse_id}`,
    );
  };

  // Fix filterColumn values to match backend
  const searchFilters = [
    { value: "all", label: "All" },
    { value: "product_code", label: "Product Code" },
    { value: "product_name", label: "Product Name" },
    { value: "unit_of_measure", label: "UOM" },
    { value: "stock_quantity", label: "Stock Quantity" },
    { value: "average_price", label: "Average Price" },
    { value: "total_price", label: "Total Price" },
  ];

  // DataTable columns mapping
  const productColumns = [
    {
      name: "Warehouse",
      selector: (row) => row.warehouse_name,
      sortField: "warehouse_name",
    },
    {
      name: "Code",
      selector: (row) => row.product_code,
      sortField: "product_code",
    },
    {
      name: "Name",
      selector: (row) => (
        <div className="text-center" title={row.product_name}>
          {row.product_name}
        </div>
      ),
      sortField: "product_name",
      width: "24rem",
    },
    {
      name: "Category",
      selector: (row) => row.product_category,
      sortField: "product_category",
    },
    {
      name: "UOM",
      selector: (row) => row.unit_of_measure,
      sortField: "unit_of_measure",
    },
    {
      name: "Threshold",
      selector: (row) => row.threshold,
      sortField: "threshold",
    },
    {
      name: "Stock Quantity",
      selector: (row) => formatToTwoDecimal(row.stock_quantity) || "N/A",
      sortField: "stock_quantity",
    },
    {
      name: "Average Price",
      selector: (row) => formatToTwoDecimal(row.average_price) || "N/A",
      sortField: "average_price",
    },
    {
      name: "Total Price",
      selector: (row) => formatToTwoDecimal(row.total_price) || "N/A",
      sortField: "total_price",
    },
  ];

  useEffect(() => {
    getWidgets();
    getProducts();
    fetchWarehouse();
  }, []);

  ///PARA ITO SA FILTER RETENTION NG PAGE
  //START
  // Save gate — stays closed until after mount restore settles,
  // so the hook's internal reset-to-1 from updateParams doesn't overwrite the saved page.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (productsPagination.currentPage) {
      FilterStore.SCTsavePage(productsPagination.currentPage);
    }
  }, [productsPagination.currentPage]);

  useEffect(() => {
    if (productsPagination.itemsPerPage) {
      FilterStore.SCTsavePerPage(productsPagination.itemsPerPage);
    }
  }, [productsPagination.itemsPerPage]);

  // Restore last viewed page on mount. setTimeout(0) defers until after
  // getProducts() → updateParams → hook's reset-to-1 has settled,
  // then opens the save gate for user navigation.
  useEffect(() => {
    const savedPage = FilterStore.SCTgetPage();
    if (savedPage > 1) {
      setTimeout(() => {
        productsPagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 0);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 0);
    }
  }, []);
  //END

  // Reset searchTerm when filterColumn changes
  // useEffect(() => {
  //   setSearchTerm();
  // }, [filterColumn]);
  return (
    <div className="h-100 w-100 bg-white custom-container">
      <div className="row">
        <div className="col-md-6">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center justify-content-center">
                <i className="bx bx-package me-1 h-100"></i>
                Total Quantity
              </h5>
              <p
                className="card-text amount"
                title={formatToTwoDecimal(widgets.totalQuantity)}
                // Same green as the Customer widgets (lighter in dark mode)
                style={{
                  cursor: "default",
                  color: "var(--widget-number-color)",
                }}>
                {formatToTwoDecimal(widgets.totalQuantity)}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center justify-content-center">
                <i className="bx bx-money me-1 h-100"></i>
                Overall Price
              </h5>
              <p
                className="card-text amount"
                title={formatToTwoDecimal(widgets.overallPrice)}
                // Same green as the Customer widgets (lighter in dark mode)
                style={{
                  cursor: "default",
                  color: "var(--widget-number-color)",
                }}>
                ₱{formatToTwoDecimal(widgets.overallPrice)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="w-100 row mx-0 mt-3">
        <div className="col-sm mb-2">
          <select
            className="form-select"
            onChange={handleWarehouseChange}
            value={selectedWarehouse}>
            <option value="">All Warehouse</option>
            {warehouseOptions.current.map((warehouse, index) => (
              <option key={index} value={warehouse.warehouse_id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm text-end mb-2"></div>
      </div>

      <div className="row mx-0 mt-2">
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button
            type="button"
            className="btn btn-outline-secondary dropdown-toggle-split"
            data-bs-toggle="dropdown"
            aria-expanded="false">
            <i className="fa-solid fa-sliders"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            {searchFilters.map(({ value, label }) => (
              <li key={value}>
                <button
                  className={`dropdown-item ${
                    filterColumn === value ? "active" : ""
                  }`}
                  onClick={() => setFilterColumn(value)}>
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {/* )} */}
      </div>
      <div className="w-100 mt-3 container-fluid">
        <div className="data-table-cell-width">
          <DataTable
            columns={sort.sortColumns(productColumns)}
            data={productsPagination.data}
            customStyles={customStyles}
            onRowClicked={handleRowClicked}
            {...sort.sortProps}
          />
          <PaginationControls {...productsPagination} />
        </div>
      </div>
    </div>
  );
}

export default NewStockCategoryTab;
